<template>
  <div class="card">
    <woot-message-editor
      v-model="noteContent"
      class="input--note"
      :placeholder="$t('NOTES.ADD.PLACEHOLDER')"
      :enable-suggestions="false"
    />
    <div class="footer">
      <woot-button
        color-scheme="warning"
        :title="$t('NOTES.ADD.TITLE')"
        :is-disabled="buttonDisabled || isUploading"
        @click="onAdd"
      >
        {{ $t('NOTES.ADD.BUTTON') }} (⌘⏎)
      </woot-button>
        <file-upload
          ref="upload"
          v-tooltip.left="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
          :accept="allowedFileTypes"
          :multiple="false"
          :drop="true"
          :drop-directory="false"
          @input-file="onUpload"
        >
          <woot-button
            class-names="button--upload"
            :title="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
            icon="attach"
            emoji="📎"
            color-scheme="secondary"
            variant="smooth"
            size="small"
            :is-disabled="isUploading"
          />
        </file-upload>
        <woot-button
          v-if="file && !isUploading"
          class="remove--attachment clear secondary"
          icon="dismiss"
          color-scheme="secondary"
          variant="smooth"
          size="small"
          @click="onFileDelete"
        />
              
    </div>
  </div>
</template>

<script>
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor';
import { hasPressedCommandAndEnter } from 'shared/helpers/KeyboardHelpers';
import FileUpload from 'vue-upload-component';
import { DirectUpload } from 'activestorage';
import {
  buildAccountDirectUploadUrl,
  buildBlobUrl,
} from 'dashboard/helper/directUploadHelper';
import {
  ALLOWED_FILE_TYPES,
} from 'shared/constants/messages';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import inboxMixin from 'shared/mixins/inboxMixin';
import { mixin as clickaway } from 'vue-clickaway';
import { mapGetters } from 'vuex';


export default {
  components: {
    WootMessageEditor,
    FileUpload
  },
  mixins: [clickaway, eventListenerMixins, uiSettingsMixin, inboxMixin],

  data() {
    return {
      noteContent: '',
      file: null,
      fileUrl: null,
      isUploading: false
    };
  },

  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentUser: 'getCurrentUser',
    }),
    buttonDisabled() {
      return this.noteContent === '' && this.file == null ? true : false;
    },
    allowedFileTypes() {
      return ALLOWED_FILE_TYPES;
    },
  },

  mounted() {
    document.addEventListener('keydown', this.onMetaEnter);
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.onMetaEnter);
  },

  methods: {
    onMetaEnter(e) {
      if (hasPressedCommandAndEnter(e)) {
        e.preventDefault();
        this.onAdd();
      }
    },
    onAdd() {
      this.$emit('add', { content: this.noteContent, file: this.file });
      this.noteContent = '';
      this.file = null;
      this.fileUrl = null;
    },

    async onUpload(target) {
      if (!target || !target.file) return;
      
      this.isUploading = true;
      bus.$emit('newToastMessage', this.$t('NOTES.UPLOAD.UPLOADING'));
      
      try {
        const uploadUrl = buildAccountDirectUploadUrl(this.accountId);
        const upload = new DirectUpload(target.file, uploadUrl, {
          directUploadWillCreateBlobWithXHR: xhr => {
            xhr.setRequestHeader('api_access_token', this.currentUser.access_token);
          },
        });

        await new Promise((resolve, reject) => {
          upload.create((error, blob) => {
            if (error) {
              reject(error);
            } else {
              this.fileUrl = buildBlobUrl(blob.signed_id, blob.filename);
              this.file = target.file;
              resolve();
            }
          });
        });
        
        bus.$emit('newToastMessage', this.$t('NOTES.UPLOAD.SUCCESS'));
      } catch (error) {
        bus.$emit('newToastMessage', this.$t('NOTES.UPLOAD.ERROR'));
        console.error('Upload error:', error);
      } finally {
        this.isUploading = false;
      }
    },

    onFileDelete() {
      this.file = null;
      this.fileUrl = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.input--note {
  &::v-deep .ProseMirror-menubar {
    padding: 0;
    margin-top: var(--space-minus-small);
  }

  &::v-deep .ProseMirror-woot-style {
    max-height: 36rem;
  }
}

.footer {
  display: flex;
  justify-content: flex-end;
  align-items: center !important;
  gap: 1rem;
  width: 100%;
}

.file-uploads {
  display: flex;
  align-items: center;
} 
</style>
